import {
  Dialog,
  FlexColumn,
  CodeEditor,
} from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import styled from 'styled-components';
import {
  fontSize,
  fontWeight,
  lineHeight,
  radius,
  space,
} from '@elementary/common-ui/style/utils';
import { GPGKey } from '../../types';
import GPGKeyDetails from '../GPGKeyDetails';

const InstructionBoxBase = styled(FlexColumn)`
  background-color: ${({ theme }) => theme.palette.surface.tinted};
  padding: ${space('lg')};
  border-radius: ${radius('lg')};
  gap: ${space('sm')};
  margin-bottom: ${space('lg')};
  margin-top: ${space('lg')};
`;

const InstructionBoxTitle = styled(Typography)`
  font-size: ${fontSize('sm')};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ theme }) => theme.palette.content.muted};
`;

const InstructionBoxContent = styled.ol`
  font-size: ${fontSize('sm')};
  color: ${({ theme }) => theme.palette.content.muted};
  white-space: pre;
  margin: 0;
  padding-left: ${space('lgPlus')};
  line-height: ${lineHeight('md')};
`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  gpgKey: GPGKey;
}

const SuccessStageDialog = ({ isOpen, onClose, gpgKey }: Props) => (
  <Dialog
    open={isOpen}
    onClose={onClose}
    title="GPG Key Generated Successfully"
    confirmText="Done"
    hideCancelButton
    customWidth={800}
    onConfirmButtonClick={onClose}
    message={
      <FlexColumn sx={{ gap: 3 }}>
        <FlexColumn sx={{ gap: 1 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: fontWeight('semibold'),
              color: 'status.success.main',
            }}
          >
            ✓ Your GPG key has been generated and is now active!
          </Typography>

          <InstructionBoxBase>
            <InstructionBoxTitle>To enable signed commits:</InstructionBoxTitle>
            <InstructionBoxContent>
              <li>Copy the public key below</li>
              <li> Go to GitHub → Settings → SSH and GPG keys</li>
              <li>Click ‘New GPG key’ and paste the public key</li>
              <li>
                Your future commits will be automatically signed with this key
              </li>
            </InstructionBoxContent>
            <Typography
              sx={{ fontSize: fontSize('sm'), color: 'content.muted' }}
            >
              Note: Keep this public key safe - you’ll need to add it to any Git
              platforms where you want signed commits to be verified.
            </Typography>
          </InstructionBoxBase>

          <GPGKeyDetails gpgKey={gpgKey} />
        </FlexColumn>

        <FlexColumn sx={{ gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: fontWeight('semibold') }}
          >
            Public Key:
          </Typography>

          <CodeEditor
            value={gpgKey.publicKey}
            mode="text"
            readOnly
            height="300px"
            hideLineNumbers
          />
        </FlexColumn>
      </FlexColumn>
    }
  />
);

export default SuccessStageDialog;
